export type SignalBreakdown =
  | {
      total_hits?: number;
      positive_hits?: number;
      negative_hits?: number;
      net_score?: number;
      has_giftcard_anchor?: boolean;
      has_offer_structure?: boolean;
      hits_by_signal?: Record<string, number>;
      rejection_reason_code?: string | null;
      decision_reason?: string | null;
    }
  | Array<{
      signal_id?: string;
      signal_name?: string;
      fired?: boolean;
      delta?: number;
      evidence?: string[];
    }>
  | null
  | undefined;

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (!normalized) continue;
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

export function deriveTriggeredSignals(
  matchedSignals: string[] | null | undefined,
  signalBreakdown: SignalBreakdown,
): string[] {
  if (Array.isArray(matchedSignals) && matchedSignals.length > 0) {
    return uniqueNonEmpty(matchedSignals);
  }

  if (Array.isArray(signalBreakdown)) {
    const fromArray = signalBreakdown
      .filter((row) => row?.fired === true && Number(row?.delta ?? 0) !== 0)
      .map((row) => row.signal_name ?? row.signal_id ?? null);
    return uniqueNonEmpty(fromArray);
  }

  if (signalBreakdown && typeof signalBreakdown === "object") {
    const hitsBySignal = (signalBreakdown as Record<string, unknown>).hits_by_signal;
    if (hitsBySignal && typeof hitsBySignal === "object" && !Array.isArray(hitsBySignal)) {
      const fired = Object.entries(hitsBySignal as Record<string, unknown>)
        .filter(([, count]) => Number(count ?? 0) > 0)
        .map(([signalId]) => signalId);
      return uniqueNonEmpty(fired);
    }
  }

  return [];
}

const REJECTION_REASON_LABELS: Record<string, string> = {
  BELOW_THRESHOLD: "Below threshold",
  MISSING_ANCHOR: "Not candidate: missing gift-card anchor",
  MISSING_OFFER_STRUCTURE: "Not candidate: missing offer-structure signal",
  OCR_INSUFFICIENT: "Not candidate: OCR insufficient for scoring",
};

export function deriveCandidateResultLabel(
  isCandidate: boolean,
  score: number,
  threshold: number,
  signalBreakdown: SignalBreakdown,
): string {
  if (isCandidate) return "Candidate (score >= threshold)";

  if (signalBreakdown && typeof signalBreakdown === "object" && !Array.isArray(signalBreakdown)) {
    const reasonCode = String((signalBreakdown as Record<string, unknown>).rejection_reason_code ?? "").trim();
    if (reasonCode && REJECTION_REASON_LABELS[reasonCode]) {
      return REJECTION_REASON_LABELS[reasonCode];
    }
  }

  if (score < threshold) return "Below threshold";

  const hasOfferStructure =
    signalBreakdown && typeof signalBreakdown === "object" && !Array.isArray(signalBreakdown)
      ? (signalBreakdown as Record<string, unknown>).has_offer_structure
      : undefined;
  if (hasOfferStructure === false) return "Not candidate: missing offer-structure signal";

  const hasGiftcardAnchor =
    signalBreakdown && typeof signalBreakdown === "object" && !Array.isArray(signalBreakdown)
      ? (signalBreakdown as Record<string, unknown>).has_giftcard_anchor
      : undefined;
  if (hasGiftcardAnchor === false) return "Not candidate: missing gift-card anchor";

  return "Not candidate";
}
